import React, { useCallback } from 'react';

import { Box, Inline } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

import { ContentOwnerItemForDetailsPanel } from '@confluence/content-ownership/entry-points/ContentOwnerItem';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';

import { useSpaceAdmin } from '../hooks/useSpaceAdmin';
import { useContentOwner } from '../hooks/useContentOwner';
import { useContentContributors } from '../hooks/useContentContributors';

import { InlineRetryError } from './InlineRetryError';

export const OwnerSection = () => {
	const {
		type: contentType,
		contentStatus,
		loading: contentStatusLoading,
		error: contentStatusError,
		refetch: contentStatusRefetch,
	} = useContentStatus();
	const {
		data: contentOwnerData,
		loading: contentOwnerLoading,
		error: contentOwnerError,
		refetch: contentOwnerRefetch,
	} = useContentOwner();
	const {
		data: spaceAdminData,
		loading: spaceAdminLoading,
		error: spaceAdminError,
		refetch: spaceAdminRefetch,
	} = useSpaceAdmin();

	const {
		data: contentContributors,
		loading: contentContributorsLoading,
		error: contentContributorsError,
		refetch: contentContributorsRefetch,
	} = useContentContributors();
	const noOtherContributors = contentContributorsLoading
		? undefined
		: contentContributors?.count === 0;

	const error =
		contentStatusError || contentOwnerError || spaceAdminError || contentContributorsError;
	const refetch = useCallback(() => {
		if (contentStatusError) {
			void contentStatusRefetch();
		}
		if (contentOwnerError) {
			void contentOwnerRefetch();
		}
		if (spaceAdminError) {
			void spaceAdminRefetch();
		}
		if (contentContributorsError) {
			void contentContributorsRefetch();
		}
	}, [
		contentStatusError,
		contentStatusRefetch,
		contentOwnerError,
		contentOwnerRefetch,
		spaceAdminError,
		spaceAdminRefetch,
		contentContributorsError,
		contentContributorsRefetch,
	]);

	const renderOwnerSectionContent = () => {
		if (contentStatusLoading || contentOwnerLoading || spaceAdminLoading) {
			return (
				<UFOLoadHold name="owner-section-details-panel-hold">
					<Inline alignBlock="center" space="space.150" testId="owner-section-loading">
						<Skeleton width="40px" height="40px" borderRadius={200} />
						<Skeleton width="154px" height="32px" borderRadius={4} />
					</Inline>
				</UFOLoadHold>
			);
		}

		if (error) {
			return <InlineRetryError error={error} refetch={refetch} />;
		}

		const { accountId, profilePicturePath, displayName } = contentOwnerData;
		return (
			<ContentOwnerItemForDetailsPanel
				ownerAccountId={accountId}
				ownerName={displayName}
				ownerAvatarUrl={profilePicturePath}
				contentType={contentType}
				contentStatus={contentStatus}
				noOtherContributors={noOtherContributors}
				isCurrentUserSpaceAdmin={spaceAdminData.isCurrentUserSpaceAdmin}
				analyticsSource="contentWrapper"
			/>
		);
	};

	return (
		<Box paddingBlockStart="space.025" testId="owner-section">
			{renderOwnerSectionContent()}
		</Box>
	);
};
